import type { HeadObjectCommandOutput, S3Client } from '@aws-sdk/client-s3';
import { type AwsCredentialIdentity } from '@aws-sdk/types';
import { type CallHomeAwsSettings } from '@solinkcorp/authentication';
import settings from '@solinkcorp/settings';

/**
 * This is a wrapper around the AWS S3 client.  Since video-discovery-web uses S3
 * very minimally, this hides the S3 client away behind code splitting, so it
 * won't be downloaded unless it's actually needed.
 *
 * IMPORTANT: @aws-sdk/client-s3 is pinned to v3.726.1 due to a bug in v3.729.0+
 * where PutObjectCommand with Blob objects fails with "getReader is not a function".
 * See: https://github.com/aws/aws-sdk-js-v3/issues/6834
 * Related Jira: SL-6931 (Floor Plan Upload failure)
 */
export class S3 {
  private credentials: AwsCredentialIdentity | undefined;

  constructor(aws: CallHomeAwsSettings) {
    this.credentials = {
      accessKeyId: aws.accessKeyId,
      secretAccessKey: aws.secretAccessKey,
      expiration: aws.expiration ? new Date(aws.expiration) : undefined,
      sessionToken: aws.sessionToken,
    };
  }

  private async _client(): Promise<S3Client> {
    const region = settings.aws_region;
    if (!region) {
      throw Error('AWS region not set');
    }
    const { S3Client } = await import('@aws-sdk/client-s3');

    // Create a fresh copy of credentials to avoid AWS SDK mutation errors on frozen objects
    return new S3Client({
      region,
      credentials: this.credentials
        ? {
            accessKeyId: this.credentials.accessKeyId,
            secretAccessKey: this.credentials.secretAccessKey,
            expiration: this.credentials.expiration,
            sessionToken: this.credentials.sessionToken,
          }
        : undefined,
    });
  }

  async putObject(params: {
    Bucket: string;
    Key: string;
    Body: string | Blob;
    ContentType: string;
  }): Promise<void> {
    const client = await this._client();
    const { PutObjectCommand } = await import('@aws-sdk/client-s3');
    await client.send(new PutObjectCommand(params));
  }

  async headObject(params: { Bucket: string; Key: string }): Promise<HeadObjectCommandOutput> {
    const client = await this._client();
    const { HeadObjectCommand } = await import('@aws-sdk/client-s3');
    return await client.send(new HeadObjectCommand(params));
  }

  async getSignedUrlExistingObject(bucket: string, key: string): Promise<string> {
    const { getSignedUrl } = await import('@aws-sdk/s3-request-presigner');
    const { GetObjectCommand } = await import('@aws-sdk/client-s3');

    return getSignedUrl(
      await this._client(),
      new GetObjectCommand({
        Key: key,
        Bucket: bucket,
      }),
    );
  }
}
